import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fileUrl, money } from "@/lib/api";
import { Swatch } from "@/lib/colors";

export const CartLines = ({ compact = false, quote = null }) => {
  const { items, update, remove } = useCart();
  return (
    <ul className="divide-y divide-white/10">
      {items.map((i) => {
        const quoted = quote?.items?.find(
          (line) => line.product_id === i.product_id && line.color === i.color,
        );
        const linePrice = quoted?.price ?? i.price;
        return (
          <li
            key={i.key}
            className="flex gap-4 py-5"
            data-testid={`cart-item-${i.key}`}
          >
            <Link
              to={`/product/${i.slug}`}
              className={`${compact ? "w-20 h-24" : "w-24 h-32"} bg-[#0f0f12] flex-shrink-0 overflow-hidden`}
            >
              {i.photo && (
                <img
                  src={fileUrl(i.photo)}
                  alt={i.name}
                  className="w-full h-full object-cover"
                />
              )}
            </Link>
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex justify-between gap-3">
                <Link
                  to={`/product/${i.slug}`}
                  className="text-sm font-medium text-white truncate hover:text-zinc-300"
                >
                  {i.name}
                </Link>
                <button
                  onClick={() => remove(i.key)}
                  data-testid={`cart-remove-${i.key}`}
                  className="text-zinc-500 hover:text-white"
                  aria-label="Remove"
                >
                  <X size={15} />
                </button>
              </div>
              <span className="flex items-center gap-2 text-xs text-zinc-400 mt-1">
                <Swatch name={i.color} size={10} /> {i.color}
              </span>
              <div className="mt-auto flex items-center justify-between pt-3">
                <div className="flex items-center border border-white/15">
                  <button
                    className="p-2 text-zinc-300 hover:text-white disabled:opacity-30"
                    disabled={i.quantity <= 1}
                    onClick={() => update(i.key, i.quantity - 1)}
                    data-testid={`cart-decrease-${i.key}`}
                    aria-label="Decrease"
                  >
                    <Minus size={12} />
                  </button>
                  <span
                    className="w-8 text-center text-sm"
                    data-testid={`cart-qty-${i.key}`}
                  >
                    {i.quantity}
                  </span>
                  <button
                    className="p-2 text-zinc-300 hover:text-white disabled:opacity-30"
                    disabled={i.quantity >= (i.max || 50)}
                    onClick={() => update(i.key, i.quantity + 1)}
                    data-testid={`cart-increase-${i.key}`}
                    aria-label="Increase"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <span className="text-sm text-white">
                  {money(linePrice * i.quantity)}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const DiscountCodeInput = ({ quote }) => {
  const { code, setCode } = useCart();
  const [value, setValue] = useState("");
  const applied =
    quote?.discount && quote.discount.method === "code" ? quote.discount : null;
  const apply = (e) => {
    e.preventDefault();
    if (value.trim()) setCode(value.trim().toUpperCase());
    setValue("");
  };
  return (
    <div data-testid="discount-code-section">
      {code ? (
        <div className="flex items-center justify-between border border-white/15 px-3 py-2.5 text-xs">
          <span
            className={`flex items-center gap-2 tracking-[0.15em] uppercase ${quote && !applied ? "text-zinc-500 line-through" : "text-white"}`}
            data-testid="discount-code-applied"
          >
            <Tag size={12} className="text-[#ff2a2a]" /> {code}
          </span>
          <button
            type="button"
            onClick={() => setCode("")}
            className="text-zinc-500 hover:text-white"
            data-testid="discount-code-remove"
            aria-label="Remove code"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply(e)}
            placeholder="Discount code"
            className="store-input py-2.5 uppercase tracking-wider text-xs"
            data-testid="discount-code-input"
          />
          <button
            type="button"
            onClick={apply}
            className="btn-ghost-store px-5 py-2.5"
            data-testid="discount-code-apply"
          >
            Apply
          </button>
        </div>
      )}
      {code && quote?.code_error && (
        <p
          className="text-xs text-[#ff6b6b] mt-2"
          data-testid="discount-code-error"
        >
          {quote.code_error}
        </p>
      )}
      {code && quote?.note && (
        <p
          className="text-xs text-zinc-400 mt-2"
          data-testid="discount-code-note"
        >
          {quote.note}
        </p>
      )}
      {applied && (
        <p
          className="text-xs text-zinc-400 mt-2"
          data-testid="discount-code-success"
        >
          Code applied.
        </p>
      )}
    </div>
  );
};

export const CartTotals = ({
  quote,
  quoteError = "",
  loading = false,
  awaitingAddress = false,
}) => {
  const { subtotal: localSub, shipping: localShip } = useCart();
  const q = quote || {
    subtotal: localSub,
    shipping_total: localShip,
    shipping_after: localShip,
    discount_amount: 0,
    shipping_discount: 0,
    handling_fee: 0,
    tax: 0,
    total: localSub + localShip,
  };
  const d = quote?.discount;
  const shippingUnavailable = quote?.shipping_details?.source === "fallback";
  const taxDisabled = quote?.tax_status === "disabled";
  const noTaxCollection = quote?.tax_status === "out_of_state_review";
  const showTaxNote =
    (!taxDisabled && !noTaxCollection) || shippingUnavailable || awaitingAddress || loading || quoteError;
  const taxReady = Boolean(quote?.tax_ready) && !loading && !awaitingAddress;
  const taxDisplayReady = taxReady && !shippingUnavailable;
  const handlingReady = Boolean(quote) && !loading;
  const taxNote = shippingUnavailable
    ? "Live shipping must be available before payment can continue."
    : awaitingAddress
    ? "Enter a valid U.S. delivery address to calculate live shipping."
    : loading
      ? "Calculating your delivery total…"
      : taxDisabled
        ? "Sales tax collection is disabled. Tax may still be legally required."
      : taxReady
        ? quote?.tax_message || "Tax calculated from the delivery address."
        : quoteError || "Tax calculation is unavailable for this address.";
  return (
    <div className="space-y-2 text-sm" data-testid="cart-totals">
      <div className="flex justify-between text-zinc-400">
        <span>Subtotal</span>
        <span className="text-white" data-testid="cart-subtotal">
          {money(q.subtotal)}
        </span>
      </div>
      {q.discount_amount > 0 && (
        <div
          className="flex justify-between text-zinc-400"
          data-testid="cart-discount-row"
        >
          <span className="flex items-center gap-1.5">
            <Tag size={11} className="text-[#ff2a2a]" />
            {d?.code || d?.title}
          </span>
          <span className="text-[#ff6b6b]" data-testid="cart-discount">
            −{money(q.discount_amount)}
          </span>
        </div>
      )}
      <div className="flex justify-between text-zinc-400">
        <span>
          Shipping
          {q.shipping_discount > 0 && d ? (
            <span className="text-zinc-500"> · {d.code || d.title}</span>
          ) : null}
        </span>
        <span className="text-white" data-testid="cart-shipping">
          {q.shipping_discount > 0 && (
            <span className="line-through text-zinc-600 mr-2">
              {money(q.shipping_total)}
            </span>
          )}
          {shippingUnavailable
            ? "Unavailable"
            : awaitingAddress || loading
            ? "Pending"
            : q.shipping_after > 0
              ? money(q.shipping_after)
              : "Free"}
        </span>
      </div>
      {handlingReady && (
        <div
          className="flex justify-between text-zinc-400"
          data-testid="cart-handling-fee-row"
        >
          <span>Handling Fee (7%)</span>
          <span className="text-white" data-testid="cart-handling-fee">
            {money(q.handling_fee || 0)}
          </span>
        </div>
      )}
      {!taxDisabled && !noTaxCollection && (
        <div
          className="flex justify-between text-zinc-400"
          data-testid="cart-tax-row"
        >
          <span>
            {quote?.tax_status === "calculated"
              ? "Florida sales tax"
              : "Sales tax"}
          </span>
          <span className="text-white" data-testid="cart-tax">
            {taxDisplayReady ? money(q.tax || 0) : "Pending"}
          </span>
        </div>
      )}
      <div className="flex justify-between pt-3 border-t border-white/10 text-white font-medium">
        <span>Total</span>
        <span data-testid="cart-total">
          {taxDisplayReady ? money(q.total) : "Pending"}
        </span>
      </div>
      {showTaxNote && (
        <p
          className={`text-xs pt-1 ${quoteError ? "text-[#ff6b6b]" : "text-zinc-500"}`}
          data-testid={quoteError ? "checkout-quote-error" : "cart-tax-note"}
        >
          {taxNote}
        </p>
      )}
    </div>
  );
};
